import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { and, eq, ne, sql } from "@-/com.npmjs.drizzle-orm";
import {
  morphemeCorpusFilterFromCacheKey,
  wordCorpusSqlCondition,
} from "@-/dev.improve.haniverse.lib.morpheme.corpus";
import { useMorphemeCorpusFilter } from "@-/dev.improve.haniverse.lib.morpheme.corpus.use";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import type { DictionaryWord } from "#use-dictionary-words-query";

export function useGetContainingWordsQueryKey() {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const { cacheKey: corpusCacheKey } = useMorphemeCorpusFilter();
  return (word: string) => ["containingWords", word, corpusCacheKey, { staticDb }] as const;
}

export const [useContainingWordsQueryOptions, containingWordsQueryFn] = createUseQueryOptions(
  useGetContainingWordsQueryKey,
  ({ queryKey: [_, word, corpusCacheKey, { staticDb }] }): Promise<DictionaryWord[]> =>
    withDeadline({ deadline: 30_000, operation: `Find words containing ${word}` }, async () => {
      if (word.length === 0) {
        return [];
      }
      // First narrow candidates through the indexed grapheme junction. instr then matches the
      // entire word literally and contiguously, including repeated characters and SQL wildcards.
      const words = await staticDb(import.meta.location)
        .selectDistinct({
          hsk2010Level: staticSchema.wordPrcData.hsk2010Level,
          hsk2021Level: staticSchema.wordPrcData.hsk2021Level,
          hsk2026Level: staticSchema.wordPrcData.hsk2026Level,
          id: staticSchema.word.id,
          rank: staticSchema.wordSubtlex.rank,
          tbclLevel: staticSchema.wordTwData.tbclLevel,
          word: staticSchema.word.word,
        })
        .from(staticSchema.wordGrapheme)
        .innerJoin(
          staticSchema.grapheme,
          eq(staticSchema.grapheme.id, staticSchema.wordGrapheme.graphemeId),
        )
        .innerJoin(staticSchema.word, eq(staticSchema.word.id, staticSchema.wordGrapheme.wordId))
        .leftJoin(
          staticSchema.wordSubtlex,
          eq(staticSchema.word.id, staticSchema.wordSubtlex.wordId),
        )
        .leftJoin(
          staticSchema.wordPrcData,
          eq(staticSchema.word.id, staticSchema.wordPrcData.wordId),
        )
        .leftJoin(staticSchema.wordTwData, eq(staticSchema.word.id, staticSchema.wordTwData.wordId))
        .where(
          and(
            eq(staticSchema.grapheme.glyph, sql`substr(${word}, 1, 1)`),
            ne(staticSchema.word.word, word),
            sql`instr(${staticSchema.word.word}, ${word}) > 0`,
            wordCorpusSqlCondition(morphemeCorpusFilterFromCacheKey(corpusCacheKey)),
          ),
        )
        .orderBy(
          sql`${staticSchema.wordSubtlex.rank} is null`,
          staticSchema.wordSubtlex.rank,
          staticSchema.word.word,
        );

      if (words.length === 0) {
        return [];
      }
      const definitions = await staticDb(import.meta.location).query.wordDefinition.findMany({
        columns: { definition: true, pinyin: true, wordId: true },
        orderBy: { index: "asc" },
        where: { wordId: { in: words.map((entry) => entry.id) } },
      });
      const entryByWordId = new Map<
        number,
        { definitions: [string, ...string[]]; pinyin: string }
      >();
      for (const definition of definitions) {
        const entry = entryByWordId.get(definition.wordId);
        if (entry === undefined) {
          entryByWordId.set(definition.wordId, {
            definitions: [definition.definition],
            pinyin: definition.pinyin,
          });
        } else {
          entry.definitions.push(definition.definition);
        }
      }
      return words.map((entry) => ({ ...entry, entry: entryByWordId.get(entry.id) ?? null }));
    }),
);
